import { ISaveInstallationGroup } from "../core/contracts/ISaveInstallationGroup";
import InstallationGroup, { EnrichedInstallationGroup } from "../core/models/InstallationGroup";
import { InstallationGroupAdmin } from "../core/models/InstallationGroupAdmin";
import { EnrichedInstallationGroupPage } from "../core/models/EnrichedInstallationGroupPage";
import { IEnumerable } from "../core/models/ModelHelpers";
import BaseService from "./BaseService";
import sortBy from "lodash/sortBy";
import { SessionCache } from "../helpers/cache";
import CustomerModel from "../core/models/Customer";
import includes from "lodash/includes";
import without from "lodash//without";
import InstallationService from "./InstallationService";
import { isArrayValid } from "../helpers/utility";
import { IInstallation } from "../core/contracts/IInstallation";
import UserService from "./UserService";
import { Pagination } from "../core/contracts/Pagination";
import { InstallationsAndGroupsSearchDTO } from "../core/models/search/InstallationsAndGroupsSearchDTO";
import { InstallationGroupsAndUsersSearchDTO } from "../core/models/search/InstallationGroupsAndUsersSearchDTO";
import { GroupsIdsAndUsersEmails } from "../core/models/search/GroupsIdsAndUsersEmails";
import { InstallationsIdsAndGroupsIds } from "../core/models/search/InstallationsIdsAndGroupsIds";
import User from "../core/models/User";

export default class InstallationGroupService extends BaseService {
  async fetchAll(): Promise<EnrichedInstallationGroup[]> {
    try {
      const APIResponse: InstallationGroup[] = await new BaseService(true).get<InstallationGroup[]>(
        "/core/InstallationGroup"
      );

      let ret: EnrichedInstallationGroup[] = APIResponse;
      if (isArrayValid(APIResponse)) {
        const installationsToFetch = APIResponse.flatMap((x) =>
          x.installationList.map((y) => y.installationId)
        ).filter((x) => x) as IEnumerable[];
        const installationsFromAPI: IInstallation[] = await new InstallationService().fetchMultiple(
          installationsToFetch
        );

        ret = APIResponse.map((x) => {
          const installationsIds = x.installationList.map((y) => y.installationId);
          return {
            ...x,
            installations: installationsFromAPI.filter((installation) =>
              installationsIds.includes(installation.installation_id)
            ),
          };
        });
      }

      return ret;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> fetchAll -> error", error);
      return Promise.reject(error);
    }
  }

  async fetchPage(pageFilters: Pagination): Promise<EnrichedInstallationGroupPage> {
    try {
      const StorageCache = new SessionCache();
      const LoggedUser: User | null = StorageCache.get("user");
      const LoggedUserId = LoggedUser?.id;

      const { CurrentPage, PageSize, Filters } = pageFilters;
      const { items, pagination } = await new BaseService(
        true
      ).getPaginated<InstallationGroupAdmin>(
        `/core/InstallationGroup/PaginatedGet?` +
          `PageNumber=${CurrentPage}` +
          `&PageSize=${PageSize}` +
          `&LoggedUserId=${LoggedUserId}` +
          // Admin Filters
          `&OrderBy=${Filters?.OrderBy}` +
          (Filters?.TextInput ? `&TextInput=${Filters?.TextInput}` : "") +
          (Filters?.Organization ? `&Organization=${Filters?.Organization}` : "") +
          (Filters?.Directorship ? `&Directorship=${Filters?.Directorship}` : "") +
          `&ExpiredGroup=${Filters?.ExpiredGroup}` +
          `&MyGroups=${Filters?.MyGroups}`
      );

      const pageInfo = pagination;
      const ret: EnrichedInstallationGroupPage = { items, pageInfo };
      return ret;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> fetchAll -> error", error);
      return Promise.reject(error);
    }
  }

  async fetchFilteredGroupsAndInstallationsIds(
    usersEmails: string[]
  ): Promise<InstallationsAndGroupsSearchDTO> {
    try {
      const APIResponse = await new BaseService(true).post<
        InstallationsAndGroupsSearchDTO,
        string[]
      >(`/core/InstallationGroup/SearchUsers`, usersEmails);
      return APIResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        "InstallationGroupService -> fetchFilteredGroupsAndInstallationsIds -> error",
        error
      );
      return Promise.reject(error);
    }
  }

  async fetchGroupsAndUsers(
    installationsIds: string[]
  ): Promise<InstallationGroupsAndUsersSearchDTO[]> {
    try {
      const APIResponse = await new BaseService(true).post<
        InstallationGroupsAndUsersSearchDTO[],
        string[]
      >(`/core/InstallationGroup/SearchInstallations`, installationsIds);
      return APIResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> fetchGroupsAndUsers -> error", error);
      return Promise.reject(error);
    }
  }

  async fetchInstallationsIds(): Promise<string[]> {
    try {
      const APIResponse: string[] = await new BaseService(true).get<string[]>(
        `/core/InstallationGroup/GetInstallationsIdsFromGroups`
      );
      return APIResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> fetchInstallationsIds -> error", error);
      return Promise.reject(error);
    }
  }

  async fetch(id: IEnumerable): Promise<InstallationGroup | EnrichedInstallationGroup> {
    const cache = new SessionCache();

    try {
      const APIResponse: InstallationGroup = await new BaseService(true).get<InstallationGroup>(
        `/core/InstallationGroup/${id}`
      );

      const installationsIds = APIResponse?.installationList?.map((x) => x.installationId) ?? [];

      const cachedCustomers: CustomerModel[] | null = cache.get("customers");
      let installationsToReturn: IInstallation[] = [];
      if (isArrayValid(cachedCustomers)) {
        const cachedInstallations = cachedCustomers?.flatMap((x) => x.installations) ?? [];
        const cachedInstallationsIds =
          (cachedInstallations?.map((x) => x?.installation_id)?.filter((x) => x) as string[]) ?? [];

        installationsToReturn =
          (cachedInstallations?.filter((x) =>
            includes(installationsIds, x?.installation_id)
          ) as IInstallation[]) ?? [];
        const installationsToFetch = without(
          installationsIds,
          ...cachedInstallationsIds
        ) as IEnumerable[];
        if (isArrayValid(installationsToFetch)) {
          try {
            const installationsFromAPI: IInstallation[] = await new InstallationService().fetchMultiple(
              installationsToFetch
            );
            if (isArrayValid(installationsFromAPI)) {
              installationsToReturn = sortBy(
                [...installationsToReturn, ...installationsFromAPI],
                "installation_id"
              ) as IInstallation[];
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.log("InstallationGroupService -> fetch → error", error);
            return Promise.reject(error);
          }
        }
      }

      const sortedResponse: EnrichedInstallationGroup = {
        ...APIResponse,
        installationList: sortBy(APIResponse.installationList, "installationId"),
        userList: sortBy(APIResponse.userList, "email"),
        permissionList: sortBy(APIResponse.permissionList, "type"),
        installations: installationsToReturn,
      };

      return sortedResponse;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> fetch -> error", error);
      return Promise.reject(error);
    }
  }

  async remove(id: IEnumerable): Promise<boolean> {
    try {
      const action = await new BaseService(true).delete<boolean, { id: IEnumerable }>(
        "/core/InstallationGroup",
        {
          id: id,
        }
      );
      // update user after making changes to groups
      await new UserService().fetch();
      return action;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> remove -> error", error);
      return Promise.reject(error);
    }
  }

  async removeUsersFromInstallationGroups(
    groupsIdsAndUsersEmails: GroupsIdsAndUsersEmails
  ): Promise<string[]> {
    try {
      return await new BaseService(true).delete<string[], GroupsIdsAndUsersEmails>(
        "/core/InstallationGroup/RemoveUsersFromGroups",
        groupsIdsAndUsersEmails
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> search -> removeUsers -> error", error);
      return Promise.reject(error);
    }
  }

  async removeInstallationsFromInstallationGroups(
    installationsIdsAndGroupsIds: InstallationsIdsAndGroupsIds
  ): Promise<boolean> {
    try {
      return await new BaseService(true).delete<boolean, InstallationsIdsAndGroupsIds>(
        "/core/InstallationGroup/RemoveInstallationsFromGroups",
        installationsIdsAndGroupsIds
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> search -> removeInstallations -> error", error);
      return Promise.reject(error);
    }
  }

  async update(params: ISaveInstallationGroup): Promise<boolean> {
    try {
      const action = await new BaseService(true).put<boolean, ISaveInstallationGroup>(
        "/core/InstallationGroup",
        params
      );
      // update user after making changes to groups
      await new UserService().fetch();
      return action;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> update -> error", error);
      return Promise.reject(error);
    }
  }

  async create(params: ISaveInstallationGroup): Promise<boolean> {
    try {
      const action = await new BaseService(true).post<boolean, ISaveInstallationGroup>(
        "/core/InstallationGroup",
        params
      );
      // update user after making changes to groups
      await new UserService().fetch();
      return action;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("InstallationGroupService -> create -> error", error);
      return Promise.reject(error);
    }
  }
}
